(function () {
  'use strict';

  /** App proxy: use events-v endpoint. */
  const PROXY_BASE = '/apps/workshop-events';
  const PROXY_BASE_FALLBACK = '/apps/workshop-event';
  const EVENTS_PATH = '/events-v';
  const DEV_MODE = typeof window !== 'undefined' && (window.__WB_DEV__ === true || window.__WB_DEBUG__ === true);
  const MIN_CARDS = 10;
  const AUTO_EXPAND_MAX_KM = 500;
  let uiAbortController = null;
  let uiRequestId = 0;

  function getRoot() {
    return document.querySelector('[data-workshop-events-ui]');
  }

  function showGeoError(root, message) {
    const el = root.querySelector('[data-workshop-events-ui-geo-error]');
    if (el) {
      el.textContent = message || '';
      el.classList.toggle('hidden', !message);
    }
  }

  function setStatus(root, text, visible) {
    const el = root.querySelector('[data-workshop-events-ui-status]');
    if (el) {
      el.textContent = text || '';
      el.classList.toggle('hidden', !visible);
    }
  }

  function setNearWrapVisible(root, visible) {
    const el = root.querySelector('[data-workshop-events-ui-near-wrap]');
    if (el) el.classList.toggle('hidden', !visible);
  }

  function setCardsWrapVisible(root, visible) {
    const el = root.querySelector('[data-workshop-events-ui-cards-wrap]');
    if (el) el.classList.toggle('hidden', !visible);
  }

  /** Event time display: ONLY ev.time_label (pure string). Wall-clock: never convert to local; no Date/Intl/toLocale*, no parsing of starts_at. */
  var _workshopTimeLabelWarned = (typeof Set !== 'undefined') ? new Set() : {};
  function getEventDisplayTime(ev) {
    if (!ev || typeof ev !== 'object') return '';
    var t = (ev.time_label != null && ev.time_label !== '') ? String(ev.time_label).trim() : '';
    if (!t) {
      var key = (ev.id != null ? String(ev.id) : '') || (ev.handle != null ? String(ev.handle) : '') || '';
      if (key && !_workshopTimeLabelWarned.has(key)) {
        _workshopTimeLabelWarned.add(key);
        console.warn('[workshop] time_label missing for event', ev.id || ev.handle || '');
      }
    }
    return t;
  }

  function getEventDisplayDate(ev) {
    if (!ev || typeof ev !== 'object') return '';
    return (
      (ev.date_label != null ? String(ev.date_label) : '') ||
      (ev.date_key != null ? String(ev.date_key) : '') ||
      ''
    ).trim();
  }

  /** Near-city "next" label: string fields only. Do NOT use next_event_starts_at for display. */
  function getNearCityNextLabel(nc) {
    if (!nc || typeof nc !== 'object') return '';
    var s = (nc.next_event_time_label != null && nc.next_event_time_label !== '') ? String(nc.next_event_time_label).trim() : '';
    if (s) return s;
    s = (nc.next_event_date_label != null && nc.next_event_date_label !== '') ? String(nc.next_event_date_label).trim() : '';
    if (s) return s;
    s = (nc.next_event_display != null && nc.next_event_display !== '') ? String(nc.next_event_display).trim() : '';
    if (s) return s;
    s = (nc.next_event_time != null && nc.next_event_time !== '') ? String(nc.next_event_time).trim() : '';
    if (s) return s;
    s = (nc.next_event_date_key != null && nc.next_event_date_key !== '') ? String(nc.next_event_date_key).trim() : '';
    return s || '';
  }

  function escapeHtml(s) {
    if (s == null) return '';
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }

  function wbUiNormalizeStatus(status) {
    if (status == null || status === '') return '';
    return String(status).toLowerCase().replace(/\s/g, '_').replace(/-/g, '_');
  }

  function wbUiCapacityRemaining(ev) {
    if (!ev || ev.capacity_remaining == null || ev.capacity_remaining === '') return null;
    var n = Number(ev.capacity_remaining);
    return Number.isFinite(n) ? n : null;
  }

  function wbUiIsSoldOut(ev) {
    if (wbUiNormalizeStatus(ev && ev.status) === 'sold_out') return true;
    var cap = wbUiCapacityRemaining(ev);
    return cap != null && cap <= 0;
  }

  function wbUiBtnIsSoldOut(btn) {
    if (!btn) return false;
    if (wbUiNormalizeStatus(btn.getAttribute('data-status')) === 'sold_out') return true;
    var capRaw = btn.getAttribute('data-capacity-remaining');
    if (capRaw === '' || capRaw == null) return false;
    var capN = Number(capRaw);
    return Number.isFinite(capN) && capN <= 0;
  }

  function wbUiShouldHide(ev) {
    var st = wbUiNormalizeStatus(ev && ev.status);
    return st === 'draft' || st === 'completed';
  }

  function groupKey(ev) {
    const v = (ev.venue || '').toString();
    const d = ev.date_key || '';
    const p = (ev.workshop_product || '').toString();
    const c = ev.city_key || '';
    return v + '|' + d + '|' + p + '|' + c;
  }

  function setEventSelection(root, eventId, eventHandle, cityKey) {
    const form = root.closest('form');
    if (!form) return;
    let inputId = form.querySelector('input[name="properties[_event_id]"]');
    let inputHandle = form.querySelector('input[name="properties[_event_handle]"]');
    if (!inputId) {
      inputId = document.createElement('input');
      inputId.setAttribute('type', 'hidden');
      inputId.setAttribute('name', 'properties[_event_id]');
      form.appendChild(inputId);
    }
    if (!inputHandle) {
      inputHandle = document.createElement('input');
      inputHandle.setAttribute('type', 'hidden');
      inputHandle.setAttribute('name', 'properties[_event_handle]');
      form.appendChild(inputHandle);
    }
    inputId.value = eventId || '';
    inputHandle.value = eventHandle || '';
    window.dispatchEvent(new CustomEvent('workshop:event-selected', {
      detail: { eventId: eventId || '', eventHandle: eventHandle || '', cityKey: cityKey || '' }
    }));
  }

  function run() {
    const root = getRoot();
    if (!root) return;
    if (root.closest('.hidden')) return;
    const productGid = (root.dataset.productGid || '').trim();

    const useLocationBtn = root.querySelector('[data-workshop-events-ui-use-location]');
    if (!useLocationBtn) return;

    let nearCities = [];
    let events = [];
    let focusedCityKey = '';

    root.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest('[data-workshop-events-ui-use-location]')) return;
      console.log('[events-ui] click');
      showGeoError(root, '');
      if (!navigator.geolocation) {
        showGeoError(root, 'Location is not supported.');
        return;
      }
      setStatus(root, 'Getting location…', true);
      setNearWrapVisible(root, false);
      setCardsWrapVisible(root, false);
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          if (uiAbortController) uiAbortController.abort();
          uiAbortController = new AbortController();
          const requestId = ++uiRequestId;
          let qs = 'lat=' + encodeURIComponent(lat) + '&lng=' + encodeURIComponent(lng) +
            '&min_cards=' + MIN_CARDS + '&auto_expand_max_km=' + AUTO_EXPAND_MAX_KM +
            (productGid ? '&product_gid=' + encodeURIComponent(productGid) : '');
          if (DEV_MODE && qs.indexOf('debug=') === -1) qs = qs + '&debug=1';
          const url = PROXY_BASE + EVENTS_PATH + '?' + qs;
          const startMs = typeof performance !== 'undefined' && performance.now ? performance.now() : 0;
          if (DEV_MODE) console.log('[WB_EVENTS] request_id=' + requestId + ' url=' + url + ' start');
          const signal = uiAbortController.signal;
          function fetchWithFallback() {
            return fetch(url, { method: 'GET', credentials: 'same-origin', signal: signal }).then(function (res) {
              if (res.status === 404) {
                if (DEV_MODE) console.log('[WB_EVENTS] request_id=' + requestId + ' 404 fallback');
                return fetch(PROXY_BASE_FALLBACK + EVENTS_PATH + '?' + qs, { method: 'GET', credentials: 'same-origin', signal: signal });
              }
              var endMs = typeof performance !== 'undefined' && performance.now ? performance.now() : 0;
              if (DEV_MODE) console.log('[WB_EVENTS] request_id=' + requestId + ' status=' + res.status + ' end ms=' + Math.round(endMs - startMs) + ' aborted=false');
              return res;
            });
          }
          try {
            fetchWithFallback()
              .then(function (res) {
                console.log('[events-ui] response status', res.status);
                console.log('[events-ui] response content-type', res.headers.get('content-type'));
                if (!res.ok) throw new Error('proxy_http_error');
                return res.text().then(function (text) { return { status: res.status, ok: res.ok, text: text }; });
              })
              .then(function (payload) {
                var text = payload && payload.text !== undefined ? payload.text : payload;
                console.log('[events-ui] response text (first 300)', text ? String(text).substring(0, 300) : '(empty)');
                var data;
                try {
                  data = typeof text === 'string' ? JSON.parse(text) : text;
                } catch (parseErr) {
                  console.error('[events-ui] JSON parse error', parseErr);
                  setStatus(root, "Couldn't load sessions. Try again.", true);
                  setNearWrapVisible(root, false);
                  setCardsWrapVisible(root, false);
                  throw parseErr;
                }
                if (!data || data.ok !== true) {
                  console.warn('[workshop-events]', data && data.error_code, data && data.message);
                  setStatus(root, (data && data.message && String(data.message).trim()) ? String(data.message).trim() : "Couldn't load sessions. Try again.", true);
                  setNearWrapVisible(root, false);
                  setCardsWrapVisible(root, false);
                  throw new Error('Invalid response');
                }
                if (DEV_MODE && data.meta && data.meta.debug && data.meta.debug.rid) console.log('[WB_EVENTS] request_id=' + requestId + ' backend_rid=' + data.meta.debug.rid);
                return { data: data, requestId: requestId };
              })
              .then(function (payload) {
                if (!payload || payload.requestId !== uiRequestId) return;
                var data = payload.data;
                setStatus(root, '', false);
                nearCities = (data.near_cities || []).slice();
                events = (data.events || []).filter(function (ev) { return !wbUiShouldHide(ev); });
              focusedCityKey = '';

              if (nearCities.length === 0 && events.length === 0) {
                setStatus(root, 'No sessions found for this location.', true);
                return;
              }

              const nearListEl = root.querySelector('[data-workshop-events-ui-near-list]');
              if (nearListEl) {
                nearListEl.innerHTML = nearCities.map(function (nc) {
                  const cityLabel = (nc.city || '') + (nc.state_code ? ', ' + nc.state_code : '');
                  const nextStr = getNearCityNextLabel(nc) ? ' — next: ' + getNearCityNextLabel(nc) : '';
                  const keyAttr = (nc.city_key || '').replace(/"/g, '&quot;');
                  return '<li class="workshop-events-ui__near-item cursor-pointer py-1 px-2 rounded hover:bg-gray-100" data-city-key="' + keyAttr + '" role="button" tabindex="0">' +
                    escapeHtml(cityLabel + nextStr) + '</li>';
                }).join('');
              }
              setNearWrapVisible(root, nearCities.length > 0);

              const bucketsEl = root.querySelector('[data-workshop-events-ui-buckets]');
              if (bucketsEl) {
                const byBucket = {};
                events.forEach(function (ev) {
                  const label = ev.bucket_label || 'Other';
                  if (!byBucket[label]) byBucket[label] = [];
                  byBucket[label].push(ev);
                });
                const bucketOrder = Object.keys(byBucket).sort();
                let html = '';
                bucketOrder.forEach(function (label) {
                  const list = byBucket[label];
                  const groups = {};
                  list.forEach(function (ev) {
                    const k = groupKey(ev);
                    if (!groups[k]) groups[k] = [];
                    groups[k].push(ev);
                  });
                  const groupKeys = Object.keys(groups);
                  html += '<div class="workshop-events-ui__bucket mb-4" data-bucket-label="' + escapeHtml(label) + '">';
                  html += '<h5 class="text-sm font-semibold mb-2">' + escapeHtml(label) + '</h5>';
                  groupKeys.forEach(function (k) {
                    const groupEvents = groups[k];
                    const first = groupEvents[0];
                    const title = (first.event_name || first.venue || 'Workshop Session').trim();
                    const dateStr = getEventDisplayDate(first) || '';
                    const cityDisplay = first.city || first.city_key || '';
                    html += '<div class="workshop-events-ui__card border rounded p-3 mb-2" data-card-city-key="' + escapeHtml(first.city_key || '') + '">';
                    html += '<div class="workshop-events-ui__card-title font-medium">' + escapeHtml(title) + '</div>';
                    html += '<div class="workshop-events-ui__card-meta text-sm text-gray-600">' + escapeHtml(dateStr) + (cityDisplay ? ' · ' + escapeHtml(cityDisplay) : '') + '</div>';
                    html += '<div class="workshop-events-ui__card-times flex flex-wrap gap-2 mt-2">';
                    groupEvents.forEach(function (ev) {
                      if (wbUiShouldHide(ev)) return;
                      const t = getEventDisplayTime(ev);
                      const expectedTime = (ev.time_label != null && ev.time_label !== '') ? String(ev.time_label).trim() : '';
                      if (expectedTime && t !== expectedTime && typeof window !== 'undefined' && window.__WB_DIAG__) {
                        console.warn('[workshop] time display must equal time_label', { id: ev.id, handle: ev.handle, time_label: ev.time_label, displayed: t });
                      }
                      const timeStr = t || '—';
                      const soldOut = wbUiIsSoldOut(ev);
                      const btnClass = 'workshop-events-ui__time-btn text-sm px-3 py-1 border rounded' +
                        (soldOut ? ' workshop-events-ui__time-btn--sold' : ' hover:bg-gray-100');
                      html += '<button type="button" class="' + btnClass + '" ' +
                        'data-event-id="' + escapeHtml(String(ev.id || '')) + '" ' +
                        'data-event-handle="' + escapeHtml(String(ev.handle || '')) + '" ' +
                        'data-city-key="' + escapeHtml(ev.city_key || '') + '" ' +
                        'data-time-label="' + escapeHtml(t || '') + '" ' +
                        'data-status="' + escapeHtml(soldOut ? 'sold_out' : wbUiNormalizeStatus(ev.status)) + '" ' +
                        'data-capacity-remaining="' + escapeHtml(ev.capacity_remaining != null ? String(ev.capacity_remaining) : '') + '"' +
                        '><span class="workshop-events-ui__time-text">' + escapeHtml(timeStr) + '</span>' + (soldOut ? ' <span class="workshop-events-ui__sold-badge">Sold out</span>' : '') + '</button>';
                    });
                    html += '</div></div>';
                  });
                  html += '</div>';
                });
                bucketsEl.innerHTML = html;
              }
              setCardsWrapVisible(root, events.length > 0);

              root.querySelectorAll('.workshop-events-ui__time-btn').forEach(function (btn) {
                btn.addEventListener('click', function () {
                  if (wbUiBtnIsSoldOut(btn)) {
                    if (typeof window.wbShowSoldOutCapacityModal === 'function') window.wbShowSoldOutCapacityModal();
                    else alert('Sorry, this event has reached capacity. Please select other available options.');
                    return;
                  }
                  var st = wbUiNormalizeStatus(btn.getAttribute('data-status'));
                  if (st && st !== 'active') return;
                  setEventSelection(root, btn.getAttribute('data-event-id') || '', btn.getAttribute('data-event-handle') || '', btn.getAttribute('data-city-key') || '');
                });
              });

              root.querySelectorAll('.workshop-events-ui__near-item').forEach(function (li) {
                li.addEventListener('click', function () {
                  focusedCityKey = (li.getAttribute('data-city-key') || '').trim();
                  root.querySelectorAll('.workshop-events-ui__near-item').forEach(function (el) { el.classList.remove('bg-gray-200'); });
                  li.classList.add('bg-gray-200');
                  var card = root.querySelector('.workshop-events-ui__card[data-card-city-key="' + CSS.escape(focusedCityKey) + '"]');
                  if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                });
              });
            })
            .catch(function (err) {
              if (DEV_MODE && err && err.name === 'AbortError') console.log('[WB_EVENTS] request_id=' + requestId + ' aborted=true');
              if (err && err.name === 'AbortError') return;
              console.error('[events-ui] fetch/reject', err);
              setStatus(root, "Couldn't load sessions. Try again.", true);
              setNearWrapVisible(root, false);
              setCardsWrapVisible(root, false);
            });
          } catch (err) {
            console.error('[events-ui] fetch threw', err);
            setStatus(root, "Couldn't load sessions. Try again.", true);
            setNearWrapVisible(root, false);
            setCardsWrapVisible(root, false);
          }
        },
        function () {
          setStatus(root, '', false);
          showGeoError(root, 'Location was denied. Enable location access to see sessions near you.');
        }
      );
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
